<script setup>

import notification from "@/utils/plugins/NotificationService";
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";
import ProjectsService from "@/api/services/projects.service";
import TicketStatusService from "@/api/services/ticket.status.service";
import EmployeeService from "@/api/services/employee.service";
import TicketPriorityService from "@/api/services/ticket.priority.service";
import TicketTypeService from "@/api/services/ticket.type.service";
import SharedTicketComments from "@/components/shared/SharedTicketComments.vue";
import TicketDocumentsField from "@/components/shared/TicketDocumentsField.vue";
import DepartmentService from "@/api/services/department.service";
import ClockifyButton from "@/components/shared/ClockifyButton.vue";
import TrackernetButton from "@/components/shared/TrackernetButton.vue";
import { ElMessageBox } from "element-plus";
import { useRouter } from "vue-router";
import { computed, onMounted, ref, watch } from "vue";

import TicketStatusHistoryService from "@/api/services/ticket.status.history.service";
import { currentStatusOf, primaryAction, selectableStatuses, statusByCode, successMessage } from "@/views/internal-tickets/ticketWorkflow";
import moment from "moment/moment";
import { filterByNormalizedText } from "@/utils/textSearch";
import { departmentReference as reference, employeeReference, departmentId as referenceId, canReassignTicket, assigneeNeedsVerification } from "./ticketAssignment";

const authStore = useAuthStore();
const uiStore = useUiStore();
const router = useRouter();

const props = defineProps({
  action: {
    type: String,
    required: true
  },
  ticket: {
    type: Object,
    required: true
  },
  isSubmitting: {
    type: Boolean,
    required: true,
  },
})

const emits = defineEmits(['getTicket', 'saveData', 'submit', 'updateProject']);
// Si no se toca la fecha, el ticket se registra con la hora a la que se guarda, no a la que se abrió el formulario
const createdAtEdited = ref(false);
const errorList = ref({});
// eslint-disable-next-line no-unused-vars
const params = ref({});
const formModel = ref(JSON.parse(JSON.stringify(props.ticket)));
const originalDepartment = props.ticket.department;
const originalAssignee = props.ticket.assignedEmployee;
if (formModel.value.department && typeof formModel.value.department === 'object') {
  formModel.value.department = formModel.value.department['@id'] || `/api/departments/${formModel.value.department.id}`;
}
const assignees = ref([]);
const assigneesLoading = ref(false);
const assigneesError = ref(false);
let assigneeRequest = 0;
// Solicitante y afectado pueden ser cualquier empleado activo; `users` (con tickets:internal) es solo para asignar
const allEmployees = ref([]);
const assignedEmployeeSearchQuery = ref('');
const requestingEmployeeSearchQuery = ref('');
const affectedEmployeeSearchQuery = ref('');
const types = ref([]);
const projects = ref([]);
// Hasta que llega el catálogo, el selector muestra ya el estado actual que viene en el ticket (no el grupo "EN CURSO")
const initialStatus = props.action === 'update' ? currentStatusOf(props.ticket.ticketStatusHistories) : null;
const states = ref(initialStatus ? [initialStatus] : []);
if (initialStatus) {
  formModel.value.status = initialStatus['@id'];
}
const priorities = ref([]);
const optionsDepartment = ref([]);
const departmentName = computed(() => optionsDepartment.value.find(item => item['@id'] === formModel.value.department)?.name ?? null);
const departmentsError = ref(false);
// eslint-disable-next-line no-unused-vars
const statusSelected = ref(null);
// Archivos elegidos al crear: se suben tras guardar el ticket (ver TicketDocumentsField)
const pendingFiles = ref([]);
// eslint-disable-next-line no-unused-vars
const errors = ref(null);
// eslint-disable-next-line no-unused-vars
const user = computed(() => authStore.getUserLogged);
// Gestiona el ticket (Sistemas/admin, el asignado o un responsable de su departamento): borra adjuntos
// de otros y ve/publica comentarios privados. Misma regla que TicketAccessVoter en el back, que la revalida
const canManageTicket = computed(() => canReassignTicket(authStore.getUserLogged, originalDepartment, originalAssignee));
const canEditManagedFields = computed(() => props.action === 'create' || canManageTicket.value);
const canReassign = canManageTicket;
const canChangeStatus = computed(() => props.action === 'create' || canManageTicket.value);

// Estado actual (del catálogo, con su código y los estados a los que puede pasar) y botón principal del flujo
const currentState = computed(() => states.value.find(state => state['@id'] === formModel.value.status) ?? null);
const statusCode = computed(() => currentState.value?.code ?? null);
const selectableStates = computed(() => selectableStatuses(states.value, currentState.value));
const startAction = computed(() => primaryAction(currentState.value));
const startButtonLabel = computed(() => startAction.value?.label ?? null);
// Último estado confirmado por el back: el selector vuelve a él si el cambio se cancela o se rechaza
let confirmedStatus = null;

// Solo el asignado imputa tiempo al ticket: al solicitante, al afectado y a quien lo gestiona sin ser el asignado no se les muestra el botón
const isAssignedUser = computed(() => {
  const assigned = employeeReference(formModel.value.assignedEmployee);
  return !!assigned && assigned === employeeReference(authStore.getUserLogged);
});
const showTimeButton = computed(() => props.action !== 'create' && isAssignedUser.value);

// Solo imputa en Clockify quien reúne las tres condiciones: ROLE_SYSTEMS, departamento Sistemas y su clave de API de Clockify
// (el login envía user.clockify solo si existe); el resto del personal lo hace en Trackernet
const usesClockify = computed(() => {
  const user = authStore.getUserLogged;
  return !!user
    && Array.isArray(user.roles) && user.roles.includes('ROLE_SYSTEMS')
    && user.department?.name?.trim().toLowerCase() === 'sistemas'
    && !!user.clockify?.['api-key'];
});

const canCreateTimeEntry = computed(() => {
  const user = authStore.getUserLogged;
  if (!user) {
    return false;
  }
  // trackernet:use: mismo permiso granular usado en router.js/AdminSidebar.vue
  // para el resto del módulo TrackerNet (backfill Version20260716105948).
  return !!user.canCreateTimeEntry || !!(Array.isArray(user.permissions) && user.permissions.includes('trackernet:use'));
})

const filteredAssignedEmployees = computed(() =>
  filterByNormalizedText(assignees.value, assignedEmployeeSearchQuery.value, employee => employee.fullName));
const filteredRequestingEmployees = computed(() => {
  return filterByNormalizedText(allEmployees.value, requestingEmployeeSearchQuery.value, employee => employee.fullName);
});
const filteredAffectedEmployees = computed(() => {
  return filterByNormalizedText(allEmployees.value, affectedEmployeeSearchQuery.value, employee => employee.fullName || `${employee.name} ${employee.lastName}`);
});

// eslint-disable-next-line no-unused-vars
const userAffected = computed(() => {
  return formModel.value &&
    formModel.value.affectedEmployee &&
    formModel.value.affectedEmployee.name + " " + formModel.value.affectedEmployee.lastName;
});
// eslint-disable-next-line no-unused-vars
const userRegister = computed(() => {
  return formModel.value &&
    formModel.value.requestingEmployee &&
    formModel.value.requestingEmployee.name + " " + formModel.value.requestingEmployee.lastName;
});

const employeeName = employee => employee
  ? (employee.fullName || [employee.name, employee.lastName].filter(Boolean).join(' ') || 'Empleado')
  : '(sin asignar)';
const projectTag = computed(() => {
  return props.ticket.tag;
})

watch(() => projectTag.value, (newVal) => {
  if (newVal) {
    formModel.value.tag = newVal;
  }
})

watch(
  () => formModel.value.requestingEmployee,
  (newValue) => {
    if (newValue) {
      let filteredUser = allEmployees.value.find(item => item['@id'] === newValue)
      if (filteredUser) {
        formModel.value.requestingEmployeeEmail = filteredUser.email
        if (!formModel.value.affectedEmployee) {
          formModel.value.affectedEmployee = filteredUser['@id']
          formModel.value.affectedEmployeeEmail = filteredUser.email
        }
      }

    }
  }
);
watch(
  () => formModel.value.affectedEmployee,
  (newValue) => {
    if (newValue) {
      let filteredUser = allEmployees.value.find(item => item['@id'] === newValue)
      if (filteredUser) {
        formModel.value.affectedEmployeeEmail = filteredUser.email
      }

    }
  }
);


onMounted(async () => {
  await uiStore.startLoading();
  try {
    await Promise.all([
      getProjects(),
      getStates(),
      getPriorities(),
      getAllEmployees(),
      getTypes(),
      getDepartments()
    ]);
    if (props.action === "update") {


      formModel.value.status = currentStatusOf(props.ticket.ticketStatusHistories)?.['@id'] ?? null;
      confirmedStatus = formModel.value.status;
      if (typeof formModel.value.ticketPriority === "object") {
        formModel.value.ticketPriority = formModel.value.ticketPriority['@id'];
      }
      if (typeof formModel.value.ticketType === "object") {
        formModel.value.clockifyTagId = formModel.value.ticketType.clockifyTagId;
        formModel.value.ticketType = formModel.value.ticketType['@id'];

      }
      if (formModel.value.assignedEmployee && typeof formModel.value.assignedEmployee === "object") {
        formModel.value.assignedEmployee = employeeReference(formModel.value.assignedEmployee);
      }
      if (props.ticket.affectedEmployee && typeof props.ticket.affectedEmployee === "object") {
        formModel.value.affectedEmployeeEmail = props.ticket.affectedEmployee.email
        formModel.value.affectedEmployee = props.ticket.affectedEmployee['@id'];
      }
      if (typeof formModel.value.requestingEmployee === "object") {
        formModel.value.requestingEmployeeEmail = formModel.value.requestingEmployee.email
        formModel.value.requestingEmployee = formModel.value.requestingEmployee['@id'];

      }
      if (formModel.value.department && typeof formModel.value.department === "object") {
        // Un departamento que ya no acepta tickets sigue apareciendo en los tickets que lo tienen
        if (!optionsDepartment.value.some(item => item['@id'] === formModel.value.department['@id'])) {
          optionsDepartment.value.push(formModel.value.department);
        }
        formModel.value.department = formModel.value.department['@id'];
      }
      if (typeof formModel.value.project === "object") {
        formModel.value.projectId = formModel.value.project.clockifyProjectId;
        formModel.value.project = formModel.value.project['@id'];
      }
      // Al abrir un ticket recién enviado, quien lo gestiona lo pasa a Abierto (el solicitante no)
      if (canManageTicket.value && currentState.value?.code === 'sent') {
        await openSentTicket();
      }

    } else {
      let now = moment(new Date());
      formModel.value.createdAt = now.format()
      formModel.value.status = states.value.find(v => v.name === "Abierto");
      formModel.value.ticketPriority = priorities.value.find(v => v.name === "Alta")['@id'];
    }
  } catch {
    notification.addNotification('No se han podido cargar los datos del formulario.', 'error', 6000);
  } finally {
    uiStore.stopLoading();
  }
})

const updateTicket = async () => {
  emits('getTicket')
}
// eslint-disable-next-line no-unused-vars

const getDepartments = async () => {
  uiStore.startLoading();
  try {
    const response = await DepartmentService.getDepartments({ acceptsTickets: true, itemsPerPage: 999 });
    if (!Array.isArray(response?.data?.["hydra:member"])) throw new Error('Departamentos no disponibles');
    optionsDepartment.value = response.data["hydra:member"];
    departmentsError.value = false;
  } catch {
    departmentsError.value = true;
  } finally {
    uiStore.stopLoading();
  }
  if (props.action === 'update' && originalDepartment && typeof originalDepartment === 'object' &&
      !optionsDepartment.value.some(item => reference(item) === reference(originalDepartment))) optionsDepartment.value.push({ ...originalDepartment, '@id': reference(originalDepartment) });
};
const loadAssignees = async (department, previous) => {
  const request = ++assigneeRequest;
  if (previous !== undefined && department !== previous) formModel.value.assignedEmployee = null;
  assignees.value = [];
  assigneesError.value = false;
  if (!department) { assigneesLoading.value = false; return; }
  assigneesLoading.value = true;
  uiStore.startLoading();
  try {
    const response = await EmployeeService.getEmployees({
      ticketAssignee: true, active: 1, 'job.department.id': referenceId(department), itemsPerPage: 9999,
    });
    if (request !== assigneeRequest) return;
    if (!response?.data) throw new Error('No se pudieron cargar los asignables');
    assignees.value = response.data['hydra:member'] || [];
    if (props.action === 'update' && department === reference(originalDepartment) &&
        originalAssignee && typeof originalAssignee === 'object' &&
        !assignees.value.some(item => employeeReference(item) === employeeReference(originalAssignee))) assignees.value.push({ ...originalAssignee, '@id': employeeReference(originalAssignee) });
  } catch {
    if (request === assigneeRequest) {
      assigneesError.value = true;
      notification.addNotification('No se pudieron cargar los empleados asignables.', 'error', 6000);
    }
  } finally {
    if (request === assigneeRequest) assigneesLoading.value = false;
    uiStore.stopLoading();
  }
};
watch(() => reference(formModel.value.department), loadAssignees, { immediate: true });
const retryAssignees = () => loadAssignees(reference(formModel.value.department), undefined);

const getProjects = async () => {
  let params = {
    archived: false,
    itemsPerPage: 99999,
    page: 1,
    "order[name]": "ASC",
    // "client.name": "Undanet"
  }
  const response = await ProjectsService.getProjects(params);
  projects.value = response.data["hydra:member"];
  /*   params["client.name"] = 'etsa'
     const etsaResponse = await ProjectsService.getProjects(params);
     let etsa = etsaResponse.data["hydra:member"].filter(item => item.id === 662);
     if(etsa.length > 0){
       projects.value.push(etsa[0]);
     }*/

}
const getStates = async () => {
  const response = await TicketStatusService.getStates();
  states.value = response.data["hydra:member"];
  return response.data["hydra:member"];
}
const getTypes = async () => {
  const response = await TicketTypeService.getTypes();
  types.value = response.data["hydra:member"];
}

const getAllEmployees = async () => {
  const response = await EmployeeService.getEmployees({ page: 1, itemsPerPage: 9999, active: 1, 'order[name]': 'ASC' });
  allEmployees.value = response.data["hydra:member"];
}
const filterAssignedEmployeesByName = (query) => {
  assignedEmployeeSearchQuery.value = query;
}
const filterRequestingEmployeesByName = (query) => {
  requestingEmployeeSearchQuery.value = query;
}
const filterAffectedEmployeesByName = (query) => {
  affectedEmployeeSearchQuery.value = query;
}
const getPriorities = async () => {
  const response = await TicketPriorityService.getPriorities();
  priorities.value = response.data["hydra:member"];
  return response.data["hydra:member"];
}

const sendStatusChange = (statusIri, successText) => TicketStatusHistoryService.createStatusHistory({ status: statusIri, ticket: props.ticket['@id'] })
  .then((res) => {
    formModel.value.status = res.data.status;
    confirmedStatus = res.data.status;
    notification.addNotification({
      type: 'success',
      title: 'Estado cambiado',
      message: successText ?? 'Ticket ' + formModel.value.tag + ' ' + formModel.value.id + ' cambiado al estado ' + states.value.find(v => v['@id'] === res.data.status)?.name + ' correctamente',
      duration: 10000
    });
    return true;
  })
  .catch((error) => {
    // El back rechaza (400) las transiciones que el flujo no permite
    formModel.value.status = confirmedStatus;
    notification.addNotification({
      type: 'error',
      message: error?.response?.data?.detail ?? error?.response?.data?.['hydra:description'] ?? 'Error al cambiar el estado',
      duration: 10000
    });
    return false;
  });

const openSentTicket = async () => {
  const open = statusByCode(states.value, 'open');
  if (open) {
    await sendStatusChange(open['@id'], successMessage('open', formModel.value.tag + ' - ' + formModel.value.id));
  }
};

// El botón de Trackernet pasa el ticket a Trabajando (si el flujo lo permite desde su estado) antes de arrancar el reloj
const canMoveToWorking = computed(() => statusCode.value === 'working' || !!currentState.value?.nextCodes?.includes('working'));
const ensureWorking = async () => {
  if (statusCode.value === 'working') {
    return true;
  }
  const working = statusByCode(states.value, 'working');
  return !!working && canMoveToWorking.value && await sendStatusChange(working['@id'], successMessage('working', formModel.value.tag + ' - ' + formModel.value.id));
};

const startButton = async () => {
  if (!startAction.value) {
    return;
  }
  const target = statusByCode(states.value, startAction.value.target);
  await sendStatusChange(target['@id'], successMessage(target.code, formModel.value.tag + ' - ' + formModel.value.id));
}

const changeState = () => {
  ElMessageBox.confirm('Vas a cambiar el estado del ticket, ¿Estás seguro?', '', {
    confirmButtonText: 'OK',
    cancelButtonText: 'Cancelar',
    type: 'warning',
  })
    .then(() => sendStatusChange(formModel.value.status))
    .catch(() => {
      formModel.value.status = confirmedStatus;
      notification.addNotification({
        type: 'info',
        message: 'Cambio de estado cancelado',
        duration: 10000
      });
    });
}

const submit = () => {
  errorList.value = {}
  let valid = true
  if ((props.action === 'create' || canReassign.value) && assigneeNeedsVerification(formModel.value.assignedEmployee, originalAssignee, assigneesLoading.value, assigneesError.value)) {
    notification.addNotification('No se han podido cargar los empleados asignables.', 'error', 6000);
    return;
  }
  if (!formModel.value.project) {
    errorList.value.project = "El campo proyecto es requerido";
    valid = false
  }
  if (!formModel.value.department) {
    errorList.value.department = "El campo departamento es requerido";
    valid = false
  }
  if (!formModel.value.ticketType) {
    errorList.value.ticketType = "El campo tipo es requerido";
    valid = false
  }
  if (!formModel.value.ticketPriority) {
    errorList.value.ticketPriority = "El campo prioridad es requerido";
    valid = false
  }
  if (!formModel.value.description) {
    errorList.value.description = "El campo descripción es requerido";
    valid = false
  }
  if (!formModel.value.affectedEmployee) {
    errorList.value.affectedEmployee = "El usuario afectado es requerido";
    valid = false
  }
  if (!formModel.value.requestingEmployee) {
    errorList.value.requestingEmployee = "El usuario solicitante es requerido";
    errorList.value.requestingEmployeeEmail = "El email del usuario solicitante es requerido";
    valid = false
  }

  if (formModel.value.phone && formModel.value.phone.length !== 9) {
    errorList.value.phone = "El teléfono debe tener 9 dígitos";
    valid = false
  }
  if (!formModel.value.issue) {
    errorList.value.issue = "El asunto es requerido";
    valid = false
  }


  if (!valid) {
    notification.addNotification({
      type: 'error',
      message: 'No se pudo completar el proceso, ha de rellenar todos los campos marcados y requeridos'
    })
  } else {
    if (props.action === 'create' && !createdAtEdited.value) {
      formModel.value.createdAt = moment().format();
    }
    formModel.value.internal = true;
    emits('saveData', formModel.value, pendingFiles.value);
    emits('submit');
  }

}

const changeProject = () => {
  emits('updateProject', formModel.value)
}


</script>

<template>
  <div class="container-fluid">
    <div class="w-100">
      <el-row :gutter="20">
        <el-col :span="6" :xs="24" class="d-flex align-items-center ticket-form-heading">
          <img src="@/assets/icons/group.svg" alt="" class="mt-3">
          <div class="title-text ml-4 mt-3">
            Tickets
          </div>
        </el-col>
        <el-col v-if="formModel" :span="18" :xs="24">
          <div>
            <el-row :gutter="20">
              <el-col :span="12" :xs="24">
                <div class="form-item">
                  <label for="project mr-2">Proyecto</label>
                  <el-select placeholder="Selecciona primero un Proyecto" filterable v-model="formModel.project"
                    clearable @change="changeProject">
                    <el-option v-for="item in projects" :key="item.id" :label="item.name + ' - ' + item.client.name"
                      :value="item['@id']">
                      <span>{{ item.name }}</span>
                      <span> - </span>
                      <span style="color: #8492A6">{{ item.client.name }}</span>
                    </el-option>
                  </el-select>
                </div>
                <p v-if="errorList && errorList.project" class="animated fadeIn invalid-feedback">
                  {{ errorList.project }}
                </p>
              </el-col>
              <el-col v-if="action !== 'create'" :span="12" :xs="24">
                <div class="form-item">
                  <label for="code">Código</label>
                  <el-input disabled :value="'[' + projectTag + '] - ' + formModel.id" />
                </div>
              </el-col>
            </el-row>
          </div>
        </el-col>
      </el-row>
    </div>
    <div v-if="formModel && formModel.project" class="card mb-5 wback"
      style="background-image: '@/assets/images/ticket-back.png';">
      <div class="card-body">
        <el-row :gutter="20">
          <el-col v-if="action !== 'create' && formModel" :span="8" :xs="24">
            <div class="form-item">
              <label for="state">Estado</label>
              <el-select @change="changeState" v-model="formModel.status" filterable
                :disabled="!canManageTicket">
                <el-option v-for="item in selectableStates" :key="item.id" :label="item.name" :value="item['@id']" />
              </el-select>
            </div>
          </el-col>
          <el-col :span="16" :xs="24">
            <div class="form-item">
              <label for="issue">Asunto</label>
              <el-input v-model="formModel.issue" />
            </div>
            <p v-if="errorList && errorList.issue" class="animated fadeIn invalid-feedback">
              {{ errorList.issue }}
            </p>
          </el-col>
          <el-col :span="8" :xs="24">
            <div class="form-item">
              <label for="typeTicket" class="w-100">Tipo</label>
              <el-select class="w-100" filterable v-model="formModel.ticketType" size="large">
                <el-option v-for="item in types" :key="item.id" :label="item.name" :value="item['@id']" />
              </el-select>
            </div>
            <p v-if="errorList && errorList.ticketType" class="animated fadeIn invalid-feedback">
              {{ errorList.ticketType }}
            </p>
          </el-col>
          <el-col :span="8" :xs="24">
            <div class="form-item">
              <label for="department" class="w-100">Departamento al que solicita</label>
              <el-select class="w-100" filterable :disabled="action !== 'create' && !canReassign" v-model="formModel.department">
                <el-option v-for="item in optionsDepartment" :key="item.id" :label="item.name" :value="item['@id']" />
              </el-select>
              <div v-if="departmentsError" role="alert">
                No se han podido cargar los departamentos.
                <button type="button" class="btn btn-link" @click="getDepartments">Reintentar</button>
              </div>
              <p v-if="errorList && errorList.department" class="animated fadeIn invalid-feedback">
                {{ errorList.department }}
              </p>
            </div>
          </el-col>
          <el-col :span="8" :xs="24">
            <div class="form-item">
              <label for="user" class="w-100">Usuario asignado</label>
              <el-select :disabled="(action !== 'create' && !canReassign) || !formModel.department || assigneesLoading || assigneesError" class="w-100" filterable clearable
                :filter-method="filterAssignedEmployeesByName" v-model="formModel.assignedEmployee">
                <el-option v-for="item in filteredAssignedEmployees" :key="item.id" :label="item.fullName" :value="item['@id']" />
              </el-select>
              <button v-if="assigneesError && (action === 'create' || canReassign)" type="button" class="btn btn-link" @click="retryAssignees">Reintentar carga de empleados</button>
            </div>
          </el-col>
          <el-col v-if="props.action === 'update' && canManageTicket && startButtonLabel" :span="8" :xs="24">
            <div class="ticket-form-inline-action">
              <button class="btn btn_yellow btn-assign" @click="startButton">
                {{ startButtonLabel }}
              </button>
            </div>
          </el-col>
        </el-row>
      </div>
    </div>


    <el-row v-if="formModel && formModel.project" :gutter="10">
      <el-col :lg="8" :xs="24">
        <div class="form-item">
          <label for="user" class="w-100">Solicitante</label>
          <el-select class="w-100" filterable :disabled="!canEditManagedFields" :filter-method="filterRequestingEmployeesByName"
            v-model="formModel.requestingEmployee">
            <el-option v-for="item in filteredRequestingEmployees" :key="item.id" :label="item.fullName" :value="item['@id']" />
          </el-select>
        </div>
        <p v-if="errorList && errorList.requestingEmployee" class="animated fadeIn invalid-feedback">
          {{ errorList.requestingEmployee }}
        </p>
      </el-col>
      <el-col :lg="8" :xs="24">
        <div class="form-item">
          <label for="phone">Teléfono/Extensión</label>
          <el-input v-model="formModel.phone" type="text" />
          <p v-if="errorList && errorList.phone" class="animated fadeIn invalid-feedback">
            {{ errorList.phone }}
          </p>
        </div>
      </el-col>
      <el-col :lg="8" :xs="24">
        <el-row :gutter="10">
          <el-col :lg="12" :xs="24">
            <div class="form-item date-picker">
              <label for="createdAt">Fecha creación</label>
              <el-date-picker v-model="formModel.createdAt" :disabled="!canEditManagedFields" type="datetime" format="DD/MM/YYYY HH:mm:ss"
                value-format="YYYY-MM-DDTHH:mm:ssZ" :picker-options="{ firstDayOfWeek: 1 }" @change="createdAtEdited = true" />
            </div>
          </el-col>
          <el-col :lg="12" :xs="24">
            <div class="form-item date-picker">
              <label for="createdAt">Fecha cierre</label>
              <el-date-picker v-model="formModel.closingDate" disabled name="from-date" type="date"
                format="DD/MM/YYYY HH:mm:ss" value-format="YYYY-MM-DD HH:mm:ss"
                :picker-options="{ firstDayOfWeek: 1 }" />
            </div>
          </el-col>
        </el-row>
      </el-col>
      <el-col :span="8" :xs="24">
        <div class="form-item">
          <label for="affectedEmployee" class="w-100">Empleado afectado</label>
          <el-select class="w-100" filterable :disabled="!canEditManagedFields" :filter-method="filterAffectedEmployeesByName"
            v-model="formModel.affectedEmployee">
            <el-option v-for="item in filteredAffectedEmployees" :key="item.id" :label="item.name + ' ' + item.lastName"
              :value="item['@id']" />
          </el-select>
        </div>
        <p v-if="errorList && errorList.affectedEmployee" class="animated fadeIn invalid-feedback">
          {{ errorList.affectedEmployee }}
        </p>
      </el-col>
      <el-col :span="8" :xs="24">
        <div class="form-item">
          <label for="equipmentTag">Etiqueta del equipo (si procede)</label>
          <el-input type="text" v-model="formModel.equipmentTag" />
        </div>
      </el-col>
      <el-col :span="8" :xs="24">
        <div class="form-item">
          <label for="affectedUserEmail">Email empleado afectado</label>
          <el-input disabled type="text" v-model="formModel.affectedEmployeeEmail" />
        </div>
        <p v-if="errorList && errorList.affectedEmployee && errorList.affectedEmployeeEmail"
          class="animated fadeIn invalid-feedback">
          {{ errorList.affectedEmployeeEmail }}
        </p>
      </el-col>
      <el-col :span="8" :xs="24">
        <div class="form-item">
          <label for="requestingEmployeeEmail">Email Solicitante</label>
          <el-input disabled type="text" v-model="formModel.requestingEmployeeEmail" />
        </div>
        <p v-if="errorList && errorList.requestingEmployee && errorList.requestingEmployeeEmail"
          class="animated fadeIn invalid-feedback">
          {{ errorList.requestingEmployeeEmail }}
        </p>
      </el-col>
      <el-col :span="8" :xs="24" v-if="showTimeButton && usesClockify">
        <ClockifyButton v-if="action !== 'create'" link="" :ticket="ticket" :clockifyProjectId="formModel.projectId"
          :clockify-tag-id="formModel.clockifyTagId" :status-code="statusCode" :can-work="canMoveToWorking" :ensure-working="ensureWorking"
          :assigned-employee="formModel.assignedEmployee" @update-ticket="updateTicket" />
      </el-col>
      <el-col :span="8" :xs="24" v-if="showTimeButton && !usesClockify && canCreateTimeEntry">
        <TrackernetButton v-if="action !== 'create'" link="" :ticket="ticket" :trackernetProjectId="formModel.project"
          :department-iri="formModel.department" :department-name="departmentName" :ticket-type-iri="formModel.ticketType" :status-code="statusCode" :can-work="canMoveToWorking" :ensure-working="ensureWorking"
          :assigned-employee="formModel.assignedEmployee" />
      </el-col>
    </el-row>
    <el-row v-if="formModel && formModel.project">
      <el-col :span="24" :xs="24" class="label-common">
        <div class="form-item">
          <label for="description">Descripción</label>
          <!-- <el-input
              v-model="formModel.description"
              :autosize="{ minRows: 8 }"
              type="textarea"
          /> -->
          <QuillEditor v-model:content="formModel.description" contentType="html" class="ql-editor quillNew"
            theme="snow" style="height: 200px;" />
        </div>
        <p v-if="errorList && errorList.description" class="animated fadeIn invalid-feedback">
          {{ errorList.description }}
        </p>
      </el-col>
    </el-row>
    <el-row v-if="formModel && formModel.project" :gutter="20">
      <el-col :span="12" :xs="24">
        <TicketDocumentsField :ticket-id="action === 'create' ? null : ticket.id" :documents="ticket.ticketDocuments || []"
          v-model:pending-files="pendingFiles" :can-manage="canManageTicket" enable-preview />
      </el-col>
      <el-col :span="12" :xs="24">
        <el-row :gutter="20">
          <el-col :span="24" :xs="24">
            <div class="form-item">
              <label for="priority" class="w-100">Prioridad</label>
              <el-select size="large" filterable v-model="formModel.ticketPriority">
                <el-option :disabled="!canChangeStatus" v-for="item in priorities" :key="item.id" :label="item.name"
                  :value="item['@id']" />
              </el-select>
              <p v-if="errorList && errorList.ticketPriority" class="animated fadeIn invalid-feedback">
                {{ errorList.ticketPriority }}
              </p>
            </div>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
    <el-row v-if="action !== 'create' && ticket.assignmentHistories?.length" :gutter="20">
      <el-col :span="24">
        <section class="assignment-history" aria-label="Historial de asignaciones">
          <h2>Historial de asignaciones</h2>
          <ol>
            <li v-for="(entry, index) in ticket.assignmentHistories" :key="entry['@id'] || entry.id || index">
              <time>{{ entry.changedAt ? moment(entry.changedAt).format('DD/MM/YYYY HH:mm') : 'Fecha desconocida' }}</time>
              · {{ entry.changedBy ? employeeName(entry.changedBy) : 'Proceso' }}
              · {{ entry.fromDepartment ? 'De ' + (entry.fromDepartment.name || '—') + ' · ' + employeeName(entry.fromEmployee) + ' a ' : 'Asignación inicial: ' }}
              {{ entry.toDepartment?.name || 'Sin departamento' }} · {{ employeeName(entry.toEmployee) }}
            </li>
          </ol>
        </section>
      </el-col>
    </el-row>
    <el-row v-if="formModel && action !== 'create' && formModel.project" :gutter="20">
      <!-- Las notas privadas solo las ve y publica quien gestiona el ticket (el back filtra igual) -->
      <el-col :span="12" :xs="24" v-if="canManageTicket">
        <SharedTicketComments class="internal-ticket-comments" title="Comentarios privados" :ticket="ticket" :is-private="true"
          placeholder="Escriba un comentario" />
      </el-col>
      <el-col :span="12" :xs="24">
        <SharedTicketComments class="internal-ticket-comments" title="Comentarios públicos" :ticket="ticket" :is-private="false"
          placeholder="Escriba un comentario" />
      </el-col>
    </el-row>
    <el-row v-if="formModel.project">
      <div class="d-flex justify-content-end align-items-center mt-lg-5 ticket-form-actions">
        <button class="btn btn_yellow search mr-5" @click="router.go(-1)">
          VOLVER
        </button>
        <button class="btn btn_darkpink search" @click.prevent="submit" :disabled="isSubmitting">
          <!--          GUARDAR-->
          {{ isSubmitting ? 'Guardando...' : 'Guardar' }}
        </button>
      </div>
    </el-row>
  </div>
</template>

<style scoped lang="scss">
.assignment-history { margin: 20px 0; overflow-wrap: anywhere; }
.assignment-history h2 { font-size: 1.4rem; }
.assignment-history ol { padding-left: 1.5rem; }
.assignment-history li { margin-bottom: 0.5rem; }
.btn-light {
  height: 42px !important;
}

.btn-assign {
  height: 42px;
  color: #474CE6;
  min-width: 200px;
  font-weight: 400;
  font-size: 20px;
  width: 300px;
  font-family: "Montserrat";
}

.search {
  height: 42px;
  width: 300px;
}

.wback {
  background-image: url('../../assets/images/ticket-back.png');
  background-repeat: no-repeat;
  background-size: contain;
}

label {
  display: inline-block;
  margin-bottom: 0.5rem;
  margin-top: 8px;
  font-family: Montserrat;
  font-weight: 800;
  line-height: 24px;
  letter-spacing: -0.05em;
  text-align: left;
}

label.checkbox-label input[type=checkbox] {
  position: relative;
  vertical-align: middle;
  bottom: 1px;
}

@media (max-width: 767px) {
  .internal-ticket-comments {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    column-gap: 12px;
  }

  .internal-ticket-comments > :deep(img) {
    width: 28px;
    height: auto;
  }

  .internal-ticket-comments :deep(.title-text) {
    margin-left: 0 !important;
    font-size: 1.35rem !important;
    text-align: left;
    overflow-wrap: anywhere;
  }

  .internal-ticket-comments :deep(.ticket-messages) {
    grid-column: 1 / -1;
    min-width: 0;
  }

  .container-fluid {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .ticket-form-heading {
    margin-bottom: 1rem;
  }

  .wback {
    background-size: 120px;
    background-position: top right;
  }

  .ticket-form-inline-action {
    margin-top: 0.5rem;
  }

  :deep(.el-select),
  :deep(.el-date-editor.el-input),
  :deep(.el-date-editor.el-input__wrapper),
  :deep(.el-input) {
    width: 100%;
  }

  .btn-assign,
  .search {
    width: 100%;
    min-width: 0;
  }

  .ticket-form-file-box {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }

  .ticket-form-actions {
    width: 100%;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1.5rem;
  }

  .ticket-form-actions .btn {
    margin-right: 0 !important;
  }
}
</style>
